import { CSSProperties, ReactElement, useEffect, useState } from "react";
import { IParentSize } from "../../../../types";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { IBBCRecordReport } from "./types";
import StyledTableRow from "../../../../components/styledTableRow";
import { formatDate, toTons } from "../../../../system/utils";

interface IProps {
  records: IBBCRecordReport[];
  parentSize: IParentSize;
}

interface ICrewData {
  billets: number | null;
  cobbles: number | null;
  bbc: number | null;
  weight: number | null;
  utilization: number | null;
}

interface IModel {
  factoryDay: number;
  crewA: ICrewData;
  crewB: ICrewData;
  crewC: ICrewData;
  crewD: ICrewData;
}

const styleBase = (opacity: number): CSSProperties => ({
  backgroundColor: `rgba(156, 170, 177, ${opacity})`
});

const widthStyle: CSSProperties = {
  width: 80,
  maxWidth: 80,
};

const styleA = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(144, 202, 249, ${opacity})`
});

const styleB = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(129, 212, 250, ${opacity})`
});

const styleC = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(79, 195, 247, ${opacity})`
});

const styleD = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(128, 222, 234, ${opacity})`
});



const BBCReport = ({ parentSize, records }: IProps): ReactElement => {
  const [model, setModel] = useState<IModel[]>([]);
  const [total, setTotal] = useState<IModel | null>(null);

  useEffect(() => {
    const recordsByDay: Record<string, IModel> = {};
    const newTotal: IModel = {
      factoryDay: 0,
      crewA: { billets: 0, cobbles: 0, bbc: 0, weight: 0, utilization: 0 },
      crewB: { billets: 0, cobbles: 0, bbc: 0, weight: 0, utilization: 0 },
      crewC: { billets: 0, cobbles: 0, bbc: 0, weight: 0, utilization: 0 },
      crewD: { billets: 0, cobbles: 0, bbc: 0, weight: 0, utilization: 0 }
    };

    records.forEach(record => {

      if (!recordsByDay[record.factoryDay]) {
        recordsByDay[record.factoryDay] = {
          factoryDay: record.factoryDay,
          crewA: { billets: null, cobbles: null, bbc: null, weight: null, utilization: null },
          crewB: { billets: null, cobbles: null, bbc: null, weight: null, utilization: null },
          crewC: { billets: null, cobbles: null, bbc: null, weight: null, utilization: null },
          crewD: { billets: null, cobbles: null, bbc: null, weight: null, utilization: null }
        };
      }

      const crew = recordsByDay[record.factoryDay][`crew${record.crew.code}` as keyof IModel] as ICrewData;
      crew.billets = record.billets;
      crew.cobbles = record.cobbles;
      crew.bbc = record.bbc;
      crew.weight = record.weight;

      const totalCrew = newTotal[`crew${record.crew.code}` as keyof IModel] as ICrewData;
      totalCrew.billets! += record.billets;
      totalCrew.cobbles! += record.cobbles;
      totalCrew.weight! += record.weight;
    });

    setModel(Object.values(recordsByDay).sort((a, b) => a.factoryDay - b.factoryDay));
    setTotal(newTotal);
  }, [records]);

  return (
    <TableContainer className="flex-grow-1" style={{ maxHeight: parentSize.height, height: parentSize.height }}>
      <Table stickyHeader={true} size={"small"}>
        <TableHead style={{ backgroundColor: "#b0bec5" }}>
          <TableRow>
            <TableCell align="center" style={styleBase(.5)} />

            <TableCell align="center" style={styleA(.5)} colSpan={5}>A</TableCell>
            <TableCell align="center" style={styleB(.5)} colSpan={5}>B</TableCell>
            <TableCell align="center" style={styleC(.5)} colSpan={5}>C</TableCell>
            <TableCell align="center" style={styleD(.5)} colSpan={5}>D</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="center" style={styleBase(.5)} >Factory day</TableCell>

            <TableCell align="center" style={styleA(.5)} >Billets</TableCell>
            <TableCell align="center" style={styleA(.5)} >Cobbles</TableCell>
            <TableCell align="center" style={styleA(.5)} >BBC</TableCell>
            <TableCell align="center" style={styleA(.5)} >Weight</TableCell>
            <TableCell align="center" style={styleA(.5)} >Util. %</TableCell>

            <TableCell align="center" style={styleB(.5)} >Billets</TableCell>
            <TableCell align="center" style={styleB(.5)} >Cobbles</TableCell>
            <TableCell align="center" style={styleB(.5)} >BBC</TableCell>
            <TableCell align="center" style={styleB(.5)} >Weight</TableCell>
            <TableCell align="center" style={styleB(.5)} >Util. %</TableCell>

            <TableCell align="center" style={styleC(.5)} >Billets</TableCell>
            <TableCell align="center" style={styleC(.5)} >Cobbles</TableCell>
            <TableCell align="center" style={styleC(.5)} >BBC</TableCell>
            <TableCell align="center" style={styleC(.5)} >Weight</TableCell>
            <TableCell align="center" style={styleC(.5)} >Util. %</TableCell>

            <TableCell align="center" style={styleD(.5)} >Billets</TableCell>
            <TableCell align="center" style={styleD(.5)} >Cobbles</TableCell>
            <TableCell align="center" style={styleD(.5)} >BBC</TableCell>
            <TableCell align="center" style={styleD(.5)} >Weight</TableCell>
            <TableCell align="center" style={styleD(.5)} >Util. %</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {model.map((row) => {

            return (
              <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`row-${row.factoryDay}`}>
                <TableCell>
                  {formatDate(row.factoryDay)}
                </TableCell>

                <TableCell align="right" style={styleA(.2)} >{row.crewA.billets}</TableCell>
                <TableCell align="right" style={styleA(.2)} >{row.crewA.cobbles}</TableCell>
                <TableCell align="right" style={styleA(.2)} >{row.crewA.bbc}</TableCell>
                <TableCell align="right" style={styleA(.2)} >{row.crewA.weight ? toTons(row.crewA.weight).toFixed(0) : ""}</TableCell>
                <TableCell align="right" style={styleA(.2)} >{row.crewA.utilization}</TableCell>

                <TableCell align="right" style={styleB(.2)} >{row.crewB.billets}</TableCell>
                <TableCell align="right" style={styleB(.2)} >{row.crewB.cobbles}</TableCell>
                <TableCell align="right" style={styleB(.2)} >{row.crewB.bbc}</TableCell>
                <TableCell align="right" style={styleB(.2)} >{row.crewB.weight? toTons(row.crewB.weight).toFixed(0) : ""}</TableCell>
                <TableCell align="right" style={styleB(.2)} >{row.crewB.utilization}</TableCell>

                <TableCell align="right" style={styleC(.2)} >{row.crewC.billets}</TableCell>
                <TableCell align="right" style={styleC(.2)} >{row.crewC.cobbles}</TableCell>
                <TableCell align="right" style={styleC(.2)} >{row.crewC.bbc}</TableCell>
                <TableCell align="right" style={styleC(.2)} >{row.crewC.weight ? toTons(row.crewC.weight).toFixed(0) : ""}</TableCell>
                <TableCell align="right" style={styleC(.2)} >{row.crewC.utilization}</TableCell>

                <TableCell align="right" style={styleD(.2)} >{row.crewD.billets}</TableCell>
                <TableCell align="right" style={styleD(.2)} >{row.crewD.cobbles}</TableCell>
                <TableCell align="right" style={styleD(.2)} >{row.crewD.bbc}</TableCell>
                <TableCell align="right" style={styleD(.2)} >{row.crewD.weight ? toTons(row.crewD.weight).toFixed(0) : ""}</TableCell>
                <TableCell align="right" style={styleD(.2)} >{row.crewD.utilization}</TableCell>
              </StyledTableRow>
            );
          })}
          {total &&
            <StyledTableRow hover={true} role="checkbox" tabIndex={-1}>
              <TableCell>Totals</TableCell>

              <TableCell align="right" style={styleA(.5)}>{total.crewA.billets}</TableCell>
              <TableCell align="right" style={styleA(.5)}>{total.crewA.cobbles}</TableCell>
              <TableCell align="right" style={styleA(.5)}>{total.crewA.bbc}</TableCell>
              <TableCell align="right" style={styleA(.5)}>{total.crewA.weight ? toTons(total.crewA.weight).toFixed(0) : ""}</TableCell>
              <TableCell align="right" style={styleA(.5)}>{total.crewA.utilization}</TableCell>

              <TableCell align="right" style={styleB(.5)}>{total.crewB.billets}</TableCell>
              <TableCell align="right" style={styleB(.5)}>{total.crewB.cobbles}</TableCell>
              <TableCell align="right" style={styleB(.5)}>{total.crewB.bbc}</TableCell>
              <TableCell align="right" style={styleB(.5)}>{total.crewB.weight ? toTons(total.crewB.weight).toFixed(0) : ""}</TableCell>
              <TableCell align="right" style={styleB(.5)}>{total.crewB.utilization}</TableCell>

              <TableCell align="right" style={styleC(.5)}>{total.crewC.billets}</TableCell>
              <TableCell align="right" style={styleC(.5)}>{total.crewC.cobbles}</TableCell>
              <TableCell align="right" style={styleC(.5)}>{total.crewC.bbc}</TableCell>
              <TableCell align="right" style={styleC(.5)}>{total.crewC.weight ? toTons(total.crewC.weight).toFixed(0) : ""}</TableCell>
              <TableCell align="right" style={styleC(.5)}>{total.crewC.utilization}</TableCell>

              <TableCell align="right" style={styleD(.5)}>{total.crewD.billets}</TableCell>
              <TableCell align="right" style={styleD(.5)}>{total.crewD.cobbles}</TableCell>
              <TableCell align="right" style={styleD(.5)}>{total.crewD.bbc}</TableCell>
              <TableCell align="right" style={styleD(.5)}>{total.crewD.weight ? toTons(total.crewD.weight).toFixed(0) : ""}</TableCell>
              <TableCell align="right" style={styleD(.5)}>{total.crewD.utilization}</TableCell>
            </StyledTableRow>
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BBCReport;